import React from 'react';
import { BookOpen, Award, Trophy, ArrowRight, LucideIcon } from 'lucide-react';

interface PracticeHubCardProps {
  title: string;
  description: string;
  badgeText: 'FREE' | 'PREMIUM';
  badgeGradient: string;
  badgeShadow: string;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  buttonBg: string;
  buttonHoverBg: string;
  buttonTextColor: string;
  buttonText: string;
  hasTrophyIconInButton?: boolean;
  xpReward?: string;
  onClick: () => void;
}

export const PracticeHubCard: React.FC<PracticeHubCardProps> = ({
  title,
  description,
  badgeText,
  badgeGradient,
  badgeShadow,
  Icon,
  iconBg,
  iconColor,
  buttonBg,
  buttonHoverBg,
  buttonTextColor,
  buttonText,
  hasTrophyIconInButton = false,
  xpReward,
  onClick
}) => {
  return (
    <div 
      onClick={onClick}
      className="group relative rounded-[24px] border border-slate-200/80 dark:border-slate-800/80 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md p-8 shadow-sm hover:shadow-xl hover:shadow-indigo-500/5 hover:-translate-y-1.5 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between h-full"
    >
      {/* Ambient hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 via-slate-500/0 to-indigo-500/0 group-hover:from-indigo-500/5 group-hover:to-slate-500/5 transition-all duration-500 pointer-events-none" />
      
      <div className="space-y-6 relative z-10 text-left">
        <div className="flex items-center justify-between">
          <div className={`h-12 w-12 rounded-2xl ${iconBg} border border-indigo-100 dark:border-indigo-900/30 flex items-center justify-center ${iconColor} group-hover:scale-110 transition-transform`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex items-center gap-2">
            {xpReward && (
              <span className="h-9 px-3 text-xs font-black font-mono text-amber-600 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full flex items-center gap-1 shadow-sm">
                ⚡ {xpReward}
              </span>
            )}
            <span 
              className="h-9 px-4 text-[13px] font-bold uppercase tracking-wider text-white rounded-full flex items-center justify-center border border-white/20 select-none"
              style={{
                background: badgeGradient,
                boxShadow: badgeShadow
              }}
            >
              {badgeText}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="font-display font-extrabold text-xl text-slate-900 dark:text-white">
            {title}
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
            {description}
          </p>
        </div>
      </div>

      <div className="pt-8 relative z-10">
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className={`w-full py-3 ${buttonBg} ${buttonHoverBg} ${buttonTextColor} rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-md shadow-indigo-500/10 cursor-pointer`}
        >
          <span>{buttonText}</span>
          {hasTrophyIconInButton ? (
            <Trophy className="h-3.5 w-3.5 text-slate-950 shrink-0" />
          ) : (
            <ArrowRight className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  );
};
